// Enhanced Authentication Service for ERP System
import { supabase } from '@/lib/supabaseClient';
import { User, Session } from '@supabase/supabase-js';

export interface UserProfile {
  id: string;
  email: string;
  role: string;
  company_ids: string[];
  is_active: boolean;
  created_at: string;
  theme_preference?: 'light' | 'dark' | 'system';
}

export interface UserRole {
  id: string;
  role: string;
  scope: string;
  permissions: Record<string, any>;
  is_active: boolean;
  expires_at?: string;
}

export interface UserSession {
  id: string;
  user_id: string;
  company_id?: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  expires_at: string;
  last_activity: string;
  is_active: boolean;
}

export interface Permission {
  name: string;
  description: string;
  category: string;
}

class AuthService {
  private currentUser: User | null = null;
  private currentSession: Session | null = null;
  private userProfile: UserProfile | null = null;
  private userRoles: UserRole[] = [];
  private sessionId: string | null = null;

  constructor() {
    this.initializeAuth();
  }

  private async initializeAuth() {
    // Get current session
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      this.currentSession = session;
      this.currentUser = session.user;
      await this.loadUserProfile();
      
      // Check if user is still active after loading profile
      if (this.userProfile && this.userProfile.is_active === false) {
        console.warn('User is not active, signing out');
        await this.signOut();
        return;
      }
      
      // Restore session info from localStorage
      this.restoreSession();
    } else {
      // No session yet at startup; in some F5 cases Supabase hydrates a bit later.
      // Do a short, bounded poll before we consider user truly signed-out.
      let tries = 0;
      while (tries < 30 && !this.currentSession) { // up to ~3s
        const { data: { session: s } } = await supabase.auth.getSession();
        if (s) {
          this.currentSession = s;
          this.currentUser = s.user;
          await this.loadUserProfile();
          this.restoreSession();
          break;
        }
        await new Promise(r => setTimeout(r, 100));
        tries++;
      }
    }

    // Listen for auth changes
    supabase.auth.onAuthStateChange(async (event, session) => {
      console.log(`🔐 Auth state changed: ${event}`, session ? 'Session exists' : 'No session');
      
      this.currentSession = session;
      this.currentUser = session?.user || null;

      if (event === 'SIGNED_IN' && session) {
        await this.loadUserProfile();
        
        // Check if user is active (only sign out when explicitly false)
        if (this.userProfile && this.userProfile.is_active === false) {
          await this.signOut();
          return;
        }
        
        await this.createSession();
      } else if (event === 'SIGNED_OUT') {
        await this.cleanup();
      } else if (event === 'TOKEN_REFRESHED') {
        console.log('🔄 Token 已自动刷新，session 保持有效');
        // Token 刷新时更新 session，但不需要重新加载用户资料
        this.currentSession = session;
      } else if (event === 'INITIAL_SESSION') {
        console.log('📝 初始 session 已加载');
        if (session) {
          await this.loadUserProfile();
        }
      }
    });
  }

  private restoreSession(): void {
    try {
      const sessionData = localStorage.getItem('userSession');
      if (sessionData) {
        const session = JSON.parse(sessionData);
        
        // Validate session integrity
        if (session.user_id === this.currentUser?.id && 
            session.session_token && 
            session.created_at) {
          
          // Check if session is not too old (24 hours max)
          const sessionAge = Date.now() - new Date(session.created_at).getTime();
          const maxAge = 24 * 60 * 60 * 1000; // 24 hours
          
          if (sessionAge < maxAge) {
            console.log('Valid session restored from localStorage');
          } else {
            console.warn('Session expired, clearing localStorage');
            localStorage.removeItem('userSession');
          }
        } else {
          console.warn('Invalid session data, clearing localStorage');
          localStorage.removeItem('userSession');
        }
      }
    } catch (error) {
      console.error('Error restoring session:', error);
      localStorage.removeItem('userSession');
    }
  }

  // ====================================================================
  // AUTHENTICATION METHODS
  // ====================================================================

  async signIn(email: string, password: string): Promise<{
    user: User | null;
    error: Error | null;
    profile: UserProfile | null;
  }> {
    try {
      // Direct login without approval validation (temporarily disabled)
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { user: null, error, profile: null };
      }

      if (data.user) {
        this.currentUser = data.user;
        this.currentSession = data.session;
        
        // Load user profile and roles
        await this.loadUserProfile();
        
        // Check if user is active (only sign out when explicitly false)
        if (this.userProfile && this.userProfile.is_active === false) {
          await this.signOut();
          return { 
            user: null, 
            error: new Error('帳戶未啟用，請聯繫管理員'), 
            profile: null 
          };
        }

        await this.createSession();

        return { 
          user: data.user, 
          error: null, 
          profile: this.userProfile 
        };
      }

      return { user: null, error: new Error('登入失敗'), profile: null };
    } catch (error: any) {
      console.error('Sign in error:', error);
      return { user: null, error, profile: null };
    }
  }

  async signUp(email: string, password: string, userData?: {
    role?: string;
    company_ids?: string[];
  }): Promise<{
    user: User | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        return { user: null, error };
      }

      // If user data provided, create profile
      if (data.user && userData) {
        await this.createUserProfile(data.user.id, {
          email,
          role: userData.role || 'user',
          company_ids: userData.company_ids || [],
        });
      }

      return { user: data.user, error: null };
    } catch (error: any) {
      return { user: null, error };
    }
  }

  async signOut(): Promise<void> {
    await this.endSession();
    await supabase.auth.signOut();
    await this.cleanup();
  }

  async resetPassword(email: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      return { error };
    } catch (error: any) {
      return { error };
    }
  }

  async updatePassword(newPassword: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      return { error };
    } catch (error: any) {
      return { error };
    }
  }

  // ====================================================================
  // PROFILE & ROLES MANAGEMENT
  // ====================================================================

  private async loadUserProfile(signal?: AbortSignal): Promise<void> {
    if (!this.currentUser) return;

    try {
      // Load user profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', this.currentUser.id)
        .single();

      if (profileError) {
        console.error('Error loading profile:', profileError);
        return;
      }

      if (!profileData) {
        return;
      }

      // Type assertion for theme_preference
      this.userProfile = {
        id: profileData.id,
        email: profileData.email || '',
        role: profileData.role || 'user',
        company_ids: profileData.company_ids || [],
        is_active: profileData.is_active ?? true,
        created_at: profileData.created_at || new Date().toISOString(),
        theme_preference: (profileData.theme_preference as 'light' | 'dark' | 'system' | undefined) || 'system',
      };

      // Load user roles
      await this.loadUserRoles(signal);
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  }

  private async loadUserRoles(signal?: AbortSignal): Promise<void> {
    // Skip user_roles loading due to infinite recursion in RLS policies
    // Use simplified role management based on profile.role instead
    console.log('Skipping user_roles table due to RLS policy issues');
    this.userRoles = [];
  }

  private async createUserProfile(userId: string, profileData: {
    email: string;
    role: string;
    company_ids: string[];
  }): Promise<void> {
    try {
      const { error } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          email: profileData.email,
          role: profileData.role,
          company_ids: profileData.company_ids,
        });

      if (error) {
        console.error('Error creating profile:', error);
      }
    } catch (error) {
      console.error('Error creating user profile:', error);
    }
  }

  // ====================================================================
  // SESSION MANAGEMENT
  // ====================================================================

  private async createSession(): Promise<void> {
    if (!this.currentUser || !this.currentSession) return;

    try {
      // Get client info
      const userAgent = navigator.userAgent;
      const companyId = localStorage.getItem('selectedCompanyId');

      // Skip session creation if user_sessions table doesn't exist
      // Store session info in localStorage instead
      localStorage.setItem('userSession', JSON.stringify({
        user_id: this.currentUser.id,
        session_token: this.currentSession.access_token,
        company_id: companyId || null,
        user_agent: userAgent,
        created_at: new Date().toISOString()
      }));
      
      console.log('Session stored in localStorage (user_sessions table not available)');
    } catch (error) {
      console.error('Error creating session:', error);
    }
  }

  private async updateSessionActivity(): Promise<void> {
    // Skip session activity update if user_sessions table doesn't exist
    try {
      const session = localStorage.getItem('userSession');
      if (session) {
        const sessionData = JSON.parse(session);
        sessionData.last_activity = new Date().toISOString();
        localStorage.setItem('userSession', JSON.stringify(sessionData));
      }
    } catch (error) {
      console.error('Error updating session activity:', error);
    }
  }

  private async endSession(): Promise<void> {
    try {
      localStorage.removeItem('userSession');
      this.sessionId = null;
    } catch (error) {
      console.error('Error ending session:', error);
    }
  }

  private async cleanup(): Promise<void> {
    this.currentUser = null;
    this.currentSession = null;
    this.userProfile = null;
    this.userRoles = [];
    this.sessionId = null;
    
    // Clear all localStorage data for security
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userId');
    localStorage.removeItem('userRole');
    localStorage.removeItem('companyIds');
    localStorage.removeItem('selectedCompanyId');
    localStorage.removeItem('selectedCompanyName');
    localStorage.removeItem('currentCompanyRole');
    localStorage.removeItem('userSession');
  }

  // ====================================================================
  // PERMISSION CHECKING
  // ====================================================================

  async hasPermission(permission: string, companyId?: string): Promise<boolean> {
    if (!this.currentUser) return false;

    try {
      const { data, error } = await supabase.rpc('user_has_permission', {
        user_id_param: this.currentUser.id,
        permission_name: permission,
        company_id_param: companyId || null,
      });

      if (error) {
        console.error('Error checking permission:', error);
        return false;
      }

      return data || false;
    } catch (error) {
      console.error('Error checking permission:', error);
      return false;
    }
  }

  async getUserEffectiveRoles(companyId?: string): Promise<UserRole[]> {
    if (!this.currentUser) return [];

    try {
      const { data, error } = await supabase.rpc('get_user_effective_roles', {
        user_id_param: this.currentUser.id,
        company_id_param: companyId || null,
      });

      if (error) {
        console.error('Error getting effective roles:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error getting effective roles:', error);
      return [];
    }
  }

  hasRole(role: string, companyId?: string): boolean {
    if (!this.userRoles.length) return false;

    return this.userRoles.some(userRole => 
      userRole.role === role && 
      userRole.is_active &&
      (!userRole.expires_at || new Date(userRole.expires_at) > new Date()) &&
      (userRole.scope === 'global' || 
       (companyId && userRole.scope === `company:${companyId}`))
    );
  }

  isAdmin(): boolean {
    return this.hasRole('admin') || this.userProfile?.role === 'admin';
  }

  isManager(companyId?: string): boolean {
    return this.isAdmin() || this.hasRole('manager', companyId);
  }

  // ====================================================================
  // GETTERS
  // ====================================================================

  getCurrentUser(): User | null {
    return this.currentUser;
  }

  getCurrentSession(): Session | null {
    return this.currentSession;
  }

  getUserProfile(): UserProfile | null {
    return this.userProfile;
  }

  getUserRoles(): UserRole[] {
    return this.userRoles;
  }

  isAuthenticated(): boolean {
    return this.currentUser !== null && this.currentSession !== null;
  }

  // ====================================================================
  // UTILITY METHODS
  // ====================================================================

  setupActivityTracking(): void {
    // Update session activity every 5 minutes
    setInterval(() => {
      if (this.sessionId) {
        this.updateSessionActivity();
      }
    }, 5 * 60 * 1000);

    // Track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    let lastActivity = Date.now();
    
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivity > 60000) { // Update if more than 1 minute since last update
        lastActivity = now;
        this.updateSessionActivity();
      }
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });
  }

  async refreshProfile(signal?: AbortSignal): Promise<void> {
    await this.loadUserProfile(signal);
  }

  async cleanupExpiredSessions(): Promise<void> {
    try {
      await supabase.rpc('cleanup_expired_sessions');
    } catch (error) {
      console.error('Error cleaning up sessions:', error);
    }
  }

  // ====================================================================
  // THEME PREFERENCE MANAGEMENT
  // ====================================================================

  /**
   * Get user's theme preference from profile
   * SAFETY: Always returns 'system' on any error to prevent app crashes
   * Returns 'system' if user is not logged in, preference is not set, or database fails
   */
  async getThemePreference(): Promise<'light' | 'dark' | 'system'> {
    // Safety: Return 'system' if user is not authenticated
    if (!this.currentUser) {
      return 'system';
    }

    try {
      // Return from cached profile if available and valid
      if (this.userProfile?.theme_preference) {
        const preference = this.userProfile.theme_preference;
        // Validate the value is one of the allowed options
        if (preference === 'light' || preference === 'dark' || preference === 'system') {
          return preference;
        }
        // If invalid value, fallback to 'system'
        console.warn('Invalid theme_preference value, falling back to system:', preference);
        return 'system';
      }

      // Fallback: fetch from database with timeout protection
      const fetchPromise = supabase
        .from('profiles')
        .select('theme_preference')
        .eq('id', this.currentUser.id)
        .single();

      // Add timeout to prevent hanging (5 seconds max)
      const timeoutPromise = new Promise<null>((resolve) => {
        setTimeout(() => resolve(null), 5000);
      });

      const result = await Promise.race([fetchPromise, timeoutPromise]);

      // If timeout occurred, return 'system'
      if (result === null) {
        console.warn('Theme preference fetch timeout, falling back to system');
        return 'system';
      }

      const { data, error } = result as { data: any; error: any };

      // Safety: Return 'system' on any database error
      if (error) {
        console.error('Error fetching theme preference:', error);
        return 'system';
      }

      // Safety: Validate and return preference, or 'system' if missing/invalid
      const preference = data?.theme_preference;
      if (preference === 'light' || preference === 'dark' || preference === 'system') {
        // Update cached profile
        if (this.userProfile) {
          this.userProfile.theme_preference = preference;
        }
        return preference;
      }

      // Invalid or missing preference, return 'system'
      return 'system';
    } catch (error) {
      // Safety: Catch all errors and return 'system' to prevent crashes
      console.error('Error getting theme preference (fallback to system):', error);
      return 'system';
    }
  }

  /**
   * Update user's theme preference in database
   * SAFETY: Non-blocking background operation - does not throw errors
   * UI is updated immediately, database update happens in background
   */
  async updateThemePreference(theme: 'light' | 'dark' | 'system'): Promise<{ error: Error | null }> {
    // Safety: Return early if user is not authenticated (non-blocking)
    if (!this.currentUser) {
      return { error: null }; // Don't throw error, just silently fail
    }

    // Safety: Validate theme value
    if (theme !== 'light' && theme !== 'dark' && theme !== 'system') {
      console.warn('Invalid theme value, ignoring update:', theme);
      return { error: null }; // Don't throw error
    }

    // Update cached profile immediately (optimistic update)
    if (this.userProfile) {
      this.userProfile.theme_preference = theme;
    }

    // Perform database update in background (non-blocking)
    // Use setTimeout to ensure it doesn't block the UI thread
    setTimeout(async () => {
      try {
        const { error } = await supabase
          .from('profiles')
          .update({ theme_preference: theme })
          .eq('id', this.currentUser!.id);

        if (error) {
          console.error('Background theme preference update failed (non-critical):', error);
          // Don't revert the cached value - UI already updated
        }
      } catch (error: any) {
        console.error('Background theme preference update error (non-critical):', error);
        // Don't throw - this is a background operation
      }
    }, 0);

    // Return immediately - operation is in background
    return { error: null };
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
